import type { FPlusEvaluationValue } from '@festina/configuration-general-types/configuration'
import type {
  FPlusCompletionItemList,
  FPlusDiagnostic,
  FPlusDiagnostics,
  FPlusHoverItemList,
  FPlusScopeOutput,
  FPlusSignatureHelpItemList,
} from '@festina/life-general-types/life'
import { notEmpty } from '@festina-life/core'
import type {
  EvaluationResult,
  FormatResult,
  HoverResult,
  SignatureHelpResult,
  SimpleEvaluationResult,
  SuggestionResult,
  ValidationResult,
} from '@festina-life/fplus-monaco'

export const validate = async (
  validate: Promise<FPlusDiagnostics | null>,
): Promise<ValidationResult> =>
  await validateFetcher(validate).then((result) => {
    if (!result) {
      return { diagnostics: [] }
    }

    return {
      diagnostics: result.map((e) => ({
        startLine: e?.startLineNumber ?? 0,
        endLine: e?.endLineNumber ?? 0,
        startColumn: e?.startColumn ?? 0,
        endColumn: e?.endColumn ?? 0,
        code: e?.code,
        message: e?.message,
        severity: e?.severity ?? 1,
        tags: e?.tags?.filter((t) => t != null) ?? [],
      })),
    }
  })

export const validateFetcher = async (
  validate: Promise<FPlusDiagnostics | null>,
): Promise<FPlusDiagnostic[]> => {
  try {
    const result = await validate

    if (result) {
      return result.diagnostics?.filter(notEmpty) ?? []
    }

    return []
  } catch {
    const noResponseError: FPlusDiagnostic = {
      _type: 'FPlusDiagnostic',
      startLineNumber: 0,
      startColumn: 0,
      endLineNumber: null,
      endColumn: null,
      message: 'Unable to reach validation server',
      code: '0',
    }

    return [noResponseError]
  }
}

export const fetchEnvironmnet = async (
  environmentFetcher: Promise<FPlusScopeOutput | null>,
): Promise<FPlusScopeOutput> => {
  const result = await elementFetcherNoFallback(environmentFetcher)
  return { ...result, _type: result._type ?? 'FPlusScopeOutput' }
}

export const format = async (
  fetcher: Promise<string[] | null>,
  fallBack: string[],
): Promise<FormatResult> =>
  await formatFetcher(fetcher, fallBack).then((result) => ({ content: result }))

export const formatFetcher = async (
  fetcher: Promise<string[] | null>,
  fallBack: string[],
): Promise<string[]> => await elementFetcher(fetcher, fallBack)

export const suggestions = async (
  fetcher: Promise<FPlusCompletionItemList | null>,
): Promise<SuggestionResult> =>
  await suggestionsFetcher(fetcher).then((result) => {
    if (!result?.items) {
      return { items: [] }
    }

    return result as SuggestionResult
  })

const suggestionsFetcher = async (
  fetcher: Promise<FPlusCompletionItemList | null>,
): Promise<FPlusCompletionItemList> => {
  const result = await itemsFetcher(fetcher)
  return { _type: 'FPlusCompletionItemList', ...result }
}

export const hover = async (
  fetcher: Promise<FPlusHoverItemList | null>,
): Promise<HoverResult> =>
  await hoverFetcher(fetcher).then((result) => {
    if (!result?.items) {
      return { items: [] }
    }

    return result as HoverResult
  })

const hoverFetcher = async (
  fetcher: Promise<FPlusHoverItemList | null>,
): Promise<FPlusHoverItemList> => {
  const result = await itemsFetcher(fetcher)
  return { _type: 'FPlusHoverItemList', ...result }
}

export const signatureHelp = async (
  fetcher: Promise<FPlusSignatureHelpItemList | null>,
): Promise<SignatureHelpResult> =>
  await signatureHelpFetcher(fetcher).then((result) => {
    if (!result?.items) {
      return { items: [] }
    }

    return result as SignatureHelpResult
  })

const signatureHelpFetcher = async (
  fetcher: Promise<FPlusSignatureHelpItemList | null>,
): Promise<FPlusSignatureHelpItemList> => {
  const result = await itemsFetcher(fetcher)
  return { _type: 'FPlusSignatureHelpItemList', ...result }
}

export const evaluate = async (
  fetcher: Promise<FPlusEvaluationValue | null>,
): Promise<EvaluationResult> =>
  await evaluationFetcer(fetcher).then((result) => ({
    ...result,
    _type: transformType(result._type),
  }))

const transformType = (type: string): string => {
  if (type === 'FPlusSimpleEvaluationValue') {
    return 'SimpleEvaluationResult'
  }

  if (type === 'FPlusEnumEvaluationValue') {
    return 'EnumEvaluationValue'
  }

  if (type === 'FPlusModelEvaluationValue') {
    return 'ModelEvaluationValue'
  }

  return type
}
const evaluationFetcer = async (
  fetcher: Promise<FPlusEvaluationValue | null>,
): Promise<FPlusEvaluationValue> => {
  try {
    return (
      (await fetcher) ??
      ({ _type: 'SimpleEvaluationResult' } as SimpleEvaluationResult)
    )
  } catch {
    return {
      _type: 'SimpleEvaluationResult',
      value: 'Could not evaluate result, check for type errors',
    } as SimpleEvaluationResult
  }
}

const itemsFetcher = async <T>(
  fetcher: Promise<T | null>,
): Promise<T | { items: [] }> => {
  try {
    const result = await fetcher

    if (result) {
      return result
    } else {
      return { items: [] }
    }
  } catch {
    return { items: [] }
  }
}

const elementFetcherNoFallback = async <T>(
  fetcher: Promise<T | null>,
): Promise<T | Record<string, undefined>> =>
  (await elementFetcher(fetcher, undefined)) ?? {}

const elementFetcher = async <T>(
  fetcher: Promise<T | null>,
  fallBack: T,
): Promise<T> => {
  try {
    const result = await fetcher

    if (result) {
      return result
    } else {
      return fallBack
    }
  } catch {
    return fallBack
  }
}
